import React, { useState } from 'react';
import cx from 'classnames';
import { useForm } from 'react-hook-form';
import './ChildForm.scss';
import ConfirmButton from '../../utils/Confirm';

const ChildForm = (props) => {
  const {
    setChildList,
    childList,
    childKey,
    childData,
    setChildKey,
    setShowForm,
    cancelRegistration,
    cancelMessage
  } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...childData,
      languageSpokenAtHome: childData?.languageSpokenAtHome || 'English',
    },
  });


  const handleSave = async (formData) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const childData = {
      ...formData,
      monthOfBirth: formData.monthOfBirth,
      createdDate: new Date(),
      createdBy: 'Registration Site',
      modifiedDate: new Date(),
      modifiedBy: 'Registration Site',
    };
    if (typeof childKey === 'number') {
      const newChildList = [...childList];
      newChildList[childKey] = { ...newChildList[childKey], ...childData };
      setChildList(newChildList);
    } else {
      setChildList([...childList, childData]);
    }
    setIsSubmitting(false);
  };

  const months = new Map([
    ['01', 'January'],
    ['02', 'February'],
    ['03', 'March'],
    ['04', 'April'],
    ['05', 'May'],
    ['06', 'June'],
    ['07', 'July'],
    ['08', 'August'],
    ['09', 'September'],
    ['10', 'October'],
    ['11', 'November'],
    ['12', 'December'],
  ]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, index) => currentYear - index);
  const genders = ['Male', 'Female', 'Other', 'Decline to state'];
  const childTypes = [
    'Right-handed',
    'Left-handed',
    'Ambidextrous',
    'Don’t know',
  ];
  const languages = [
    'English',
    'Spanish',
    'French',
    'German',
    'Italian',
    'Greek',
    'Chinese (Mandarin)',
    'Chinese (Cantonese)',
    'Taiwanese',
    'Japanese',
    'Korean',
    'ASL',
    'Tagalog',
    'Telugo',
    'Hebrew',
    'Filipino',
    'Urdu',
    'Russian',
    'Serbian',
    'Portuguese',
    'Armenian',
    'Hungarian',
    'Slovak',
    'Romanian',
    'Other',
  ];
  return (
    <div className="child-form form-group">
      <h2 className="page-subtitle">Child information</h2>
      <p className="required-notice">
        <span>*</span> Required Fields
      </p>
      <form onSubmit={handleSubmit(handleSave)}>
        <div
          className={cx('field-group', {
            'validation-errors': errors.firstName,
          })}
        >
          <label htmlFor="firstName">
            <span>*</span>Child's first name
          </label>
          <input
            type="text"
            maxLength={31}
            {...register('firstName', {
              required: true,
              pattern: /^[a-zA-Z .'"\-—]+$/i,
              maxLength: 31,
            })}
          />
          {errors.firstName && (
            <span className="error-label">
              {errors.firstName.type === 'required'
                ? "Please enter your child's first name"
                : "First name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."}
            </span>
          )}
        </div>

        <div
          className={cx('field-group', {
            'validation-errors': errors.gender,
          })}
        >
          <label htmlFor="gender">
            <span>*</span>Gender
          </label>
          <select {...register('gender', { required: true })}>
            <option value="">Select Gender</option>
            {genders.map((gender, index) => (
              <option key={index} value={gender}>
                {gender}
              </option>
            ))}
          </select>
          {errors.gender && (
            <span className="error-label">Please select your child’s gender</span>
          )}
        </div>

        <div className="date-form-group">
          <div
            className={cx('field-group', {
              'validation-errors': errors.monthOfBirth,
            })}
          >
            <label htmlFor="monthOfBirth">
              <span>*</span>Month of birth
            </label>
            <select {...register('monthOfBirth', { required: true })}>
              <option value="">Select Month</option>
              {Array.from(months).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.monthOfBirth && (
              <span className="error-label">Please select your child’s month of birth</span>
            )}
          </div>

          <div
            className={cx('field-group', {
              'validation-errors': errors.yearOfBirth,
            })}
          >
            <label htmlFor="yearOfBirth">
              <span>*</span>Year of birth
            </label>
            <select {...register('yearOfBirth', { required: true })}>
              <option value="">Select Year</option>
              {years.map((year, index) => (
                <option key={index} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {errors.yearOfBirth && (
              <span className="error-label">Please select your child’s year of birth</span>
            )}
          </div>
        </div>
        <div
          className={cx('field-group', {
            'validation-errors': errors.whichHandId,
          })}
        >
          <label htmlFor="whichHandId">
            <span>*</span>My child is
          </label>
          <select {...register('whichHandId', { required: true })}>
            {childTypes.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.whichHandId && (
            <span className="error-label">Please select your child’s hand preference</span>
          )}
        </div>

        <div
          className={cx('field-group', {
            'validation-errors': errors.languageSpokenAtHome,
          })}
        >
          <label htmlFor="languageSpokenAtHome">
            <span>*</span>Language spoken at home
          </label>
          <select {...register('languageSpokenAtHome', { required: true })}>
            <option value="">Select Language</option>
            {languages.map((language, index) => (
              <option key={index} value={language}>
                {language}
              </option>
            ))}
          </select>
          {errors.languageSpokenAtHome && (
            <span className="error-label">Please select the language spoken at home</span>
          )}
        </div>

        <div
          className={cx('field-group', {
            'validation-errors': errors.additionalLanguage1,
          })}
        >
          <label htmlFor="additionalLanguage1">Additional Language 1</label>
          <select {...register('additionalLanguage1', { required: false })}>
            <option value="">Select Language</option>
            {languages.map((language, index) => (
              <option key={index} value={language}>
                {language}
              </option>
            ))}
          </select>
          {errors.additionalLanguage1 && (
            <span className="error-label">Please select additional language spoken</span>
          )}
        </div>

        <div
          className={cx('field-group', {
            'validation-errors': errors.additionalLanguage2,
          })}
        >
          <label htmlFor="additionalLanguage2">Additional Language 2</label>
          <select {...register('additionalLanguage2', { required: false })}>
            <option value="">Select Language</option>
            {languages.map((language, index) => (
              <option key={index} value={language}>
                {language}
              </option>
            ))}
          </select>
          {errors.additionalLanguage2 && (
            <span className="error-label">Please select additional language spoken</span>
          )}
        </div>

        <div className="field-group button-group">
          <ConfirmButton
            buttonText="Cancel"
            buttonClassName="cancel secondary"
            confirmTitle="Cancel"
            confirmMessage={cancelMessage}
            confirm={cancelRegistration}
          />
          <button className="button primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChildForm;
